import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import { createQuery } from '@shared/lib/query-helpers';

export type CrmCoverageSummary =
  operations['getApiV1CrmAnalytics']['responses'][200]['content']['application/json'];
export type CrmCoverageAccounts =
  operations['getApiV1CrmAccounts']['responses'][200]['content']['application/json'];
export type CrmCoverageAccount = CrmCoverageAccounts['items'][number];
export type CrmCoverageAccountsQuery = NonNullable<
  operations['getApiV1CrmAccounts']['parameters']['query']
>;

const metricSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('exact'),
    value: z.number().int().nonnegative(),
    calculatedAt: z.iso.datetime(),
  }),
  z.object({ state: z.literal('unavailable'), value: z.null(), calculatedAt: z.null() }),
]);
export const crmCoverageSummarySchema: z.ZodType<CrmCoverageSummary> = z.object({
  data: z.object({
    metrics: z.object({
      totalProfiles: metricSchema,
      totalAccounts: metricSchema,
      profilesWithoutAccounts: metricSchema,
      accountsWithoutProfiles: metricSchema,
      systemAdministrators: metricSchema,
      systemDevelopers: metricSchema,
      systemClients: metricSchema,
    }),
  }),
  meta: z.object({ scope: z.literal('molchanovs'), maxAgeSeconds: z.literal(30) }),
});
export const crmCoverageAccountsSchema: z.ZodType<CrmCoverageAccounts> = z.object({
  items: z.array(
    z.object({
      accountId: z.uuid(),
      createdAt: z.iso.datetime().nullable(),
      email: z.discriminatedUnion('state', [
        z.object({ state: z.literal('visible'), value: z.string() }),
        z.object({ state: z.enum(['redacted', 'unavailable']), value: z.null() }),
      ]),
    })
  ),
  meta: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});

export const crmCoverageKeys = {
  all: ['crm-account-coverage'] as const,
  summary: (accessScope: string) => [...crmCoverageKeys.all, 'summary', accessScope] as const,
  accounts: (accessScope: string, query: CrmCoverageAccountsQuery) =>
    [...crmCoverageKeys.all, 'accounts', accessScope, query] as const,
};
export function crmCoverageSummaryQuery(accessScope: string, enabled = true) {
  return createQuery({
    queryKey: crmCoverageKeys.summary(accessScope),
    path: '/api/v1/crm/analytics',
    schema: crmCoverageSummarySchema,
    enabled: Boolean(accessScope) && enabled,
    staleTime: 30_000,
    gcTime: 0,
    retry: false,
  });
}
export function crmCoverageAccountsQuery(
  accessScope: string,
  query: CrmCoverageAccountsQuery,
  enabled = true
) {
  return createQuery({
    queryKey: crmCoverageKeys.accounts(accessScope, query),
    path: '/api/v1/crm/accounts',
    schema: crmCoverageAccountsSchema,
    requestOptions: { params: query },
    enabled: Boolean(accessScope) && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
}
